import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {DayOfWeek} from '@/features/users-availability/types'
import React, {FC, Fragment, useCallback, useMemo, useEffect} from 'react'
import {Controller, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {AvailabilityFormValidationSchema} from '@/features/users-availability/utils'
import {DaySwitchRow, TimeSlotsList} from './DayAvailabilityAtoms'
import {StyledDayContainer} from './style'
import {GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Button} from '@/components/ui/button/Button'
import {PlusIcon} from '@/components/ui/icon/Icon'

interface DayAvailabilityProps {
    day: DayOfWeek
}

export const DayAvailability: FC<DayAvailabilityProps> = React.memo(
    ({day}) => {
        const {control, formState} = useFormContext<AvailabilityFormValidationSchema>()

        const isEnabled = useWatch({
            control,
            name: `${day}.enabled`
        })

        const {fields, append, remove} = useFieldArray({
            control,
            name: `${day}.timeSlots`,
            shouldUnregister: false,
            keyName: 'id'
        })

        // Get errors for this day
        const dayErrors = formState.errors[day]
        const timeSlotsError =
            dayErrors && 'timeSlots' in dayErrors ? (dayErrors.timeSlots?.message as string) : undefined

        const handleAddTimeSlot = useCallback(() => {
            append({start: '', end: ''})
        }, [append])

        // Auto-add first slot when day is enabled
        useEffect(() => {
            if (isEnabled && fields.length === 0) {
                handleAddTimeSlot()
            }
        }, [isEnabled, fields.length, handleAddTimeSlot])

        const timeSlotsList = useMemo(
            () => <TimeSlotsList fields={fields} dayName={day} onRemoveSlot={remove} error={timeSlotsError} />,
            [fields, day, remove, timeSlotsError]
        )

        return (
            <StyledDayContainer columns={3} gap={2}>
                <GridItem spanColumn={1} fullWidth>
                    <DaySwitchRow day={day} control={control} />
                </GridItem>
                <GridItem spanColumn={2} fullWidth>
                    <Controller
                        control={control}
                        name={`${day}.enabled`}
                        render={({field}) => (
                            <Fragment>
                                {field.value && (
                                    <Flexbox direction="row" gap={3} fullWidth align="start">
                                        <Flexbox direction="column" gap={3} fullWidth>
                                            {timeSlotsList}
                                        </Flexbox>
                                        <Button
                                            variant="secondary"
                                            size="md"
                                            shape="square"
                                            onClick={handleAddTimeSlot}
                                            disabled={!field.value}
                                        >
                                            <PlusIcon />
                                        </Button>
                                    </Flexbox>
                                )}
                            </Fragment>
                        )}
                    />
                </GridItem>
            </StyledDayContainer>
        )
    },
    (prevProps, nextProps) => prevProps.day === nextProps.day
)

DayAvailability.displayName = 'DayAvailability'
